import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Code } from '#components/atoms/code/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useDeleteHostMutation } from './useDeleteHostMutation.ts';

type Props = {
  hostId: string;
  onCancel: () => void;
  onDelete: () => void;
  /**
   * When deleting the last self-hosted lighthouse on a network covered by managed lighthouses, reassure
   * the user that connectivity is preserved (the scary "last lighthouse" interstitial is skipped upstream).
   */
  managedLighthouseReassurance?: boolean;
};

export function DeleteHostDialog({ hostId, onCancel, onDelete, managedLighthouseReassurance }: Props) {
  const deleteHost = useDeleteHostMutation();
  const host = useSuspenseHostQuery(hostId).data.data;
  const hostType = host.isLighthouse ? 'lighthouse' : 'host';

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {host.name}?</div>}
      acceptButtonText="Delete"
      onCancel={onCancel}
      onAccept={() => {
        deleteHost.mutate(hostId, {
          onSuccess: onDelete,
        });
      }}
      disableAccept={deleteHost.isPending}
      shouldRestoreFocusViaFocusScope={true}
    >
      {managedLighthouseReassurance ?
        <Banner variant="info" className="mb-24">
          Deleting your last self-hosted lighthouse will not disrupt connectivity. Managed lighthouses still cover this
          network.
        </Banner>
      : null}
      <p className="p mt-0">
        Once a {hostType} is deleted it will be removed from the admin panel and blocked from accessing other hosts on
        your network.
      </p>
      <p className="p mb-0">
        Are you sure you want to delete <Code className="ml-6 wrap-anywhere">{host.name}</Code> (
        {host.ipAddresses.map((ip, idx) => (
          <span key={ip}>
            {idx > 0 && ', '}
            <Code>{ip}</Code>
          </span>
        ))}
        )?
      </p>
    </ConfirmDialog>
  );
}
